import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import useAppStore from '../store';
import { Bell, Download, X, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface AppVersion {
  id: string;
  version_number: string;
  version_name: string | null;
  file_path: string;
  file_size: number | null;
  release_notes: string | null;
  upload_date: string;
  download_count: number;
}

interface AppNotification {
  id: string;
  version_id: string;
  viewed_at: string | null;
  downloaded_at: string | null;
  version?: AppVersion;
}

const AppUpdateNotification: React.FC = () => {
  const { currentUser } = useAppStore();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!currentUser) return;

    loadNotifications();

    const subscription = supabase
      .channel('app_notifications_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'app_notifications',
          filter: `user_id=eq.${currentUser.id}`
        },
        () => {
          loadNotifications();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [currentUser]);

  const loadNotifications = async () => {
    if (!currentUser) return;

    try {
      const { data: notificationsData, error: notifError } = await supabase
        .from('app_notifications')
        .select('id, version_id, viewed_at, downloaded_at')
        .eq('user_id', currentUser.id)
        .order('created_at', { ascending: false });

      if (notifError) throw notifError;

      if (notificationsData && notificationsData.length > 0) {
        const versionIds = notificationsData.map(n => n.version_id);

        const { data: versionsData, error: versError } = await supabase
          .from('app_versions')
          .select('*')
          .in('id', versionIds)
          .eq('is_active', true);

        if (versError) throw versError;

        const notificationsWithVersions = notificationsData
          .map(notif => ({
            ...notif,
            version: versionsData?.find(v => v.id === notif.version_id)
          }))
          .filter(notif => notif.version);

        setNotifications(notificationsWithVersions);

        const unread = notificationsWithVersions.filter(n => !n.viewed_at).length;
        setUnreadCount(unread);
      } else {
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (err: any) {
      console.error('Error loading notifications:', err);
    }
  };

  const markAsViewed = async (notificationId: string, versionId: string) => {
    try {
      await supabase.rpc('mark_notification_viewed', {
        p_version_id: versionId,
        p_user_id: currentUser?.id
      });

      await loadNotifications();
    } catch (err: any) {
      console.error('Error marking notification as viewed:', err);
    }
  };

  const handleDownload = async (notification: AppNotification) => {
    if (!notification.version || !currentUser) return;

    try {
      setDownloading(notification.version.id);
      setError(null);

      const { data, error } = await supabase.storage
        .from('apk-files')
        .download(notification.version.file_path);

      if (error) throw error;

      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AppliGerbe_${notification.version.version_number}.apk`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      await supabase.rpc('increment_download_count', {
        p_version_id: notification.version.id,
        p_user_id: currentUser.id
      });

      await loadNotifications();
    } catch (err: any) {
      console.error('Error downloading APK:', err);
      setError('Erreur lors du téléchargement. Veuillez réessayer.');
    } finally {
      setDownloading(null);
    }
  };

  const formatFileSize = (bytes: number | null): string => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const handleOpenModal = () => {
    setShowModal(true);
    notifications.forEach(notif => {
      if (!notif.viewed_at && notif.version) {
        markAsViewed(notif.id, notif.version.id);
      }
    });
  };

  if (notifications.length === 0) {
    return null;
  }

  return (
    <>
      <button
        onClick={handleOpenModal}
        className="relative p-2 rounded-lg hover:bg-blue-700 transition"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
            {unreadCount}
          </span>
        )}
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Bell className="h-6 w-6" />
                Mises à jour disponibles
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4">
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                {notifications.map((notification) => {
                  if (!notification.version) return null;

                  const { version } = notification;

                  return (
                    <div
                      key={notification.id}
                      className={`border rounded-lg p-4 ${
                        notification.downloaded_at
                          ? 'bg-gray-50 border-gray-200'
                          : 'bg-blue-50 border-blue-200'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-bold text-lg">
                            Version {version.version_number}
                          </h3>
                          {version.version_name && (
                            <p className="text-sm text-gray-600">{version.version_name}</p>
                          )}
                        </div>
                        {notification.downloaded_at && (
                          <span className="flex items-center gap-1 text-green-600 text-sm">
                            <CheckCircle className="h-4 w-4" />
                            Téléchargée
                          </span>
                        )}
                      </div>

                      {version.release_notes && (
                        <div className="mb-3">
                          <p className="text-sm font-medium text-gray-700 mb-1">Notes de version:</p>
                          <p className="text-sm text-gray-600 whitespace-pre-wrap">
                            {version.release_notes}
                          </p>
                        </div>
                      )}

                      <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                        <span>Taille: {formatFileSize(version.file_size)}</span>
                        <span>
                          Publiée le {format(new Date(version.upload_date), 'dd/MM/yyyy', { locale: fr })}
                        </span>
                      </div>

                      <button
                        onClick={() => handleDownload(notification)}
                        disabled={downloading === version.id}
                        className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {downloading === version.id ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Téléchargement...
                          </>
                        ) : (
                          <>
                            <Download className="h-4 w-4" />
                            {notification.downloaded_at ? 'Télécharger à nouveau' : 'Télécharger'}
                          </>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AppUpdateNotification;
